import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { AssignmentList, SourceTypeEnum } from '../../../../../js/api';
import { Avatar } from '../../../components/avatar';
import { StatusPill } from '../../../components/StatusPill';
import { TooltippedText } from '../../../components/tooltipped-text';
import { WorkflowScopes } from '../../../constants/workflows';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';

const DATE_FORMAT = 'LLL d, yyyy';

export const AssignmentCard: React.FC<{
  assignment: AssignmentList;
  isSelected?: boolean;
  showOrganization?: boolean;
  showManagedBy?: boolean;
}> = ({ assignment, isSelected = false, showOrganization = false, showManagedBy = false }) => {
  const dateRange = [
    assignment.start_on ? format(parseISO(assignment.start_on), DATE_FORMAT) : null,
    assignment.end_on ? format(parseISO(assignment.end_on), DATE_FORMAT) : null,
  ]
    .filter(Boolean)
    .join(' - ');
  const shift_count =
    assignment.active_shift_count > 0
      ? assignment.active_shift_count
      : assignment.removed_shift_count || 0;
  const managerFullName = assignment.managed_by
    ? `${assignment.managed_by.first_name || ''} ${assignment.managed_by.last_name || ''}`.trim()
    : '';

  return (
    <div
      className={twMerge(
        clsx(
          'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-brand-black-2 tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer',
          isSelected && 'tw:!bg-muted tw:!shadow-md'
        )
      )}
    >
      <div className="tw:p-3 tw:flex tw:flex-col tw:h-full">
        <div className="tw:flex tw:justify-between tw:items-start tw:mb-3">
          <div>
            <i className="far fa-lg fa-calendar-lines-pen tw:text-zinc-700 tw:mr-1" />
            <span className="tw:text-sm tw:text-zinc-700">{assignment.code}</span>
          </div>
          <StatusPill
            scope={WorkflowScopes.Assignment}
            status={assignment.current_status || 'Unknown'}
          />
        </div>

        <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
          <Avatar
            src={assignment.provider?.profile_picture || undefined}
            className="tw:size-8 tw:shrink-0 tw:rounded-lg"
            iconSize="md"
            square={true}
          />
          <span className="tw:lg:text-lg tw:font-medium tw:text-brand-green-0">
            {assignment.provider.full_name}
          </span>
        </div>

        <div className="tw:flex tw:flex-col tw:text-sm tw:text-zinc-500 tw:mb-1 tw:gap-y-1">
          {showOrganization && assignment.healthcare_entity?.name && (
            <div className="tw:flex tw:items-center tw:gap-1">
              <i className="fas fa-fw fa-building tw:text-zinc-500 tw:mr-1" />
              <span>{assignment.healthcare_entity.name}</span>
            </div>
          )}
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="fas fa-fw fa-hospital tw:text-zinc-500 tw:mr-1" />
            {(() => {
              const facilities = (assignment as any).metadata?.shift_facilities;
              const first = facilities?.[0]?.facility_name || assignment.facility.name;
              const extra = facilities?.length > 1 ? facilities.slice(1) : [];
              return (
                <span className="">
                  {first}
                  {extra.length > 0 && (
                    <TooltippedText
                      tooltipContent={
                        <div className="tw:flex tw:flex-col tw:gap-0.5">
                          {extra.map((f: any) => (
                            <span key={f.facility_id}>{f.facility_name}</span>
                          ))}
                        </div>
                      }
                      placement="bottom"
                    >
                      <span className="tw:ml-1 tw:text-xs tw:text-brand-green-1 tw:cursor-help tw:whitespace-nowrap">
                        + {extra.length} more
                      </span>
                    </TooltippedText>
                  )}
                </span>
              );
            })()}
          </div>
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="fas fa-fw fa-map-marker-alt tw:text-zinc-500 tw:mr-1" />
            <span>{getFacilityLocationDisplayValue(assignment.facility)}</span>
          </div>
          <div className="tw:flex tw:items-center tw:text-sm tw:text-zinc-500 tw:gap-1">
            <i className="far fa-fw fa-calendar tw:mr-1" />
            <span>{dateRange}</span>
          </div>
          <div className="tw:flex tw:items-center tw:text-sm tw:text-zinc-500 tw:gap-1">
            <i className="far fa-fw fa-clock tw:mr-1" />
            {shift_count} {shift_count === 1 ? 'shift' : 'shifts'}
            {assignment.source_type === SourceTypeEnum.ORGANIZATION && (
              <span className={clsx('tw:ml-3', SOURCE_TYPE_ORGANIZATION_BADGE_STYLE)}>1099</span>
            )}
          </div>
        </div>

        {showManagedBy && (
          <div className="tw:mt-auto tw:pt-2 tw:flex tw:justify-end">
            {assignment.managed_by ? (
              <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:rounded-full tw:bg-zinc-100 tw:border tw:border-zinc-400/50 tw:text-xs tw:text-zinc-600 tw:font-medium">
                <i className="fa-solid fa-user" />
                {managerFullName || '--'}
              </span>
            ) : (
              <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:rounded-full tw:bg-white tw:border tw:border-dashed tw:border-zinc-400 tw:text-xs tw:text-zinc-500 tw:italic">
                <i className="fa-solid fa-user-plus" />
                Unassigned
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
